import { Grid, Chip, Typography, TextField } from "@mui/material";
import { useAppSelector } from "hooks/reduxHooks";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import * as yup from "yup";
import { getCoachTeams } from "repositories/teamsApi";
import CloseIcon from "@mui/icons-material/Close";

import { MultiSelect } from "components/SportSelect/MultiSelect";

import { useValidEmail } from "hooks/useValidEmail";
import { AuthContext } from "contexts/authContext";
import { useState, useContext, useEffect } from "react";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { Button } from "components/Buttons";
import { createPatientOnInvitePromise } from "repositories/patientApi";
import { USER_GROUPS } from "constants/roles";

const selectorStyle = {
  border: "1px",
  height: "34px",
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  borderColor: "#E5E5E5",
  fontSize: "14px",
  color: "#5C5D69",
  width: "100%",
  marginTop: "20px",
};
const chipStyle = {
  backgroundColor: "#161728",
  borderRadius: 10,
  margin: 3,
  color: "#FFFFFF",
};
const closeIcon = {
  color: "white",
  fontSize: "1.2em",
  stroke: "white",
  strokeWidth: 2,
};

const SportTeamSelectors = ({
  currentClinician,
  selectedSports,
  setSelectedSports,
  selectedTeams,
  setSelectedTeams,
  teams,
}: {
  currentClinician: any;
  selectedSports: any;
  setSelectedSports: any;
  selectedTeams: any;
  setSelectedTeams: any;
  teams: any;
}) => {
  return (
    <>
      <MultiSelect
        options={currentClinician?.profileInfo?.sports ?? []}
        selectedOptions={selectedSports}
        onItemSelected={(_: any, sport: string) => {
          const sportExists = selectedSports?.find(
            (selectedSport: string) => selectedSport === sport
          );
          if (sportExists) {
            return setSelectedSports(
              selectedSports.filter(
                (selectedSport: string) => selectedSport !== sport
              )
            );
          } else {
            setSelectedSports([...selectedSports, sport]);
          }
        }}
        paddingTop={0}
        hint={"Sport list"}
        menuSubTitle={"(M = Men's, W = Women's)"}
        menuTitle={""}
        style={selectorStyle}
        emptyMessage="Please select your sport in Coach Tools -> Sport preferences"
      />
      {selectedSports.map((tag: string) => (
        <Grid item justifyContent="space-between">
          <Chip
            key={tag}
            style={chipStyle}
            deleteIcon={<CloseIcon style={closeIcon} />}
            label={tag}
            variant="outlined"
            onDelete={() => {
              setSelectedSports(
                selectedSports.filter(
                  (selectedSport: string) => selectedSport !== tag
                )
              );
            }}
          />
        </Grid>
      ))}
      <MultiSelect
        options={teams}
        selectedOptions={selectedTeams ?? []}
        onItemSelected={(_: any, team: string) => {
          const teamExists = selectedTeams?.find(
            (selectedTeam: string) => selectedTeam === team
          );
          if (teamExists) {
            return setSelectedTeams(
              selectedTeams.filter(
                (selectedTeam: string) => selectedTeam !== team
              )
            );
          } else {
            setSelectedTeams([...selectedTeams, team]);
          }
        }}
        paddingTop={0}
        hint={"Teams list"}
        style={selectorStyle}
        emptyMessage="Please select team "
      />
      {selectedTeams.map((tag: string) => (
        <Grid item justifyContent="space-between">
          <Chip
            key={tag}
            style={chipStyle}
            deleteIcon={<CloseIcon style={closeIcon} />}
            label={tag}
            variant="outlined"
            onDelete={() => {
              setSelectedTeams(
                selectedTeams.filter(
                  (selectedTeam: string) => selectedTeam !== tag
                )
              );
            }}
          />
        </Grid>
      ))}
    </>
  );
};

interface Team {
  _id: string;
  name: string;
}

const InviteAthletePage = () => {
  const { email, setEmail, emailIsValid } = useValidEmail("");
  const [emailList, setEmailList] = useState("");
  const [error, setError] = useState({
    error: false,
    message: "",
    section: "",
  });
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState("");
  const [errorInvites, setErrorInvites] = useState([]);
  const [selectedSportsSingle, setSelectedSportsSingle] = useState<string[]>(
    []
  );
  const [selectedTeamsSingle, setSelectedTeamsSingle] = useState([]);
  const [selectedSportsBatch, setSelectedSportsBatch] = useState<string[]>([]);
  const [selectedTeamsBatch, setSelectedTeamsBatch] = useState([]);
  const currentClinician = useAppSelector(selectCurrentClinician);
  const authContext = useContext(AuthContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [fullTeams, setFullTeams] = useState<Team[]>([]);

  const getTeams = async () => {
    if (!currentClinician) return;
    try {
      const teams = await getCoachTeams(currentClinician._id);
      setTeams(teams.map((team: any) => team.name));
      setFullTeams(teams);
    } catch (error) {
      setTeams([]);
    }
  };
  useEffect(() => {
    getTeams();
  }, []);
  const inviteAthlete = async (
    email: string,
    sports: string[],
    selectedTeams: string[]
  ) => {
    if (!email || !currentClinician) {
      return;
    }
    const teamIds = [];
    for (const team of selectedTeams) {
      const foundTeam = fullTeams.find(
        (teamItem: Team) => teamItem.name === team
      );
      if (foundTeam) {
        teamIds.push(foundTeam?._id);
      }
    }
    const emailSchema = yup.object({
      email: yup.string().trim().lowercase().email().required(),
    });
    const normalizedEmail = email?.normalize("NFKC").trim().toLowerCase();
    const isValid = emailSchema.isValidSync({ email: normalizedEmail });
    if (!isValid) {
      throw new Error("Invalid email");
    }
    await createPatientOnInvitePromise({
      email: normalizedEmail,
      clinicianId: currentClinician._id,
      clinicId: currentClinician.clinicId ?? "",
      role: [USER_GROUPS.ATHLETE],
      profileInfo: {
        sports: sports || [],
        teams: teamIds || [],
      },
    });
    await authContext.signUpAdmin(normalizedEmail);
    await authContext.adminAddUserToGroup(normalizedEmail, USER_GROUPS.ATHLETE);
  };

  const handleSendInvite = async () => {
    setError({ error: false, message: "", section: "" });
    setLoading("invite");
    try {
      await inviteAthlete(
        email || "",
        selectedSportsSingle,
        selectedTeamsSingle
      );
      setSuccess("invite");
    } catch (error: any) {
      setSuccess("");

      setError({
        error: true,
        message: error.response?.data?.message || error?.message,
        section: "invite",
      });
    }
    setLoading("");
  };
  const handleSendBatchInvite = async () => {
    setLoading("batch");
    setError({ error: false, message: "", section: "" });

    const lastChar = emailList.charAt(emailList.length - 1);
    let parsedEmailList = emailList;
    if (lastChar == ",") {
      parsedEmailList = parsedEmailList.substring(
        0,
        parsedEmailList.length - 1
      );
    }

    const errorInvites = [];
    let successFullInvites = 0;
    const emails = parsedEmailList
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    for (const emailItem of emails) {
      try {
        const cleaned = emailItem
          ?.normalize("NFKC")
          .replace(/\s+/g, "")
          .toLowerCase();
        await inviteAthlete(cleaned, selectedSportsBatch, selectedTeamsBatch);
        if (emailItem) {
          successFullInvites = successFullInvites + 1;
        }
      } catch (error: any) {
        console.log("error", error);
        console.log("error", error.message);
        errorInvites.push(emailItem);
      }
    }
    if (successFullInvites > 0 && errorInvites.length === 0) {
      setSuccess("batch");
    } else {
      setSuccess("");
    }

    if (errorInvites.length > 0) {
      setError({
        error: true,
        message: `Batch invite sent successfully for ${successFullInvites} emails, failed for ${errorInvites.length} emails`,
        section: "batch",
      });
    }
    setLoading("");
  };

  return (
    <Grid
      style={{ padding: "150px", display: "flex", justifyContent: "center" }}
    >
      <Grid
        item
        xs={5}
        style={{ borderRight: "1px solid grey", paddingRight: "20px" }}
      >
        <h2
          style={{
            marginBottom: "20px",
            fontFamily: "Sofia Pro",
            fontSize: "24px",
          }}
        >
          Invite Athlete
        </h2>
        <Typography
          style={{
            marginBottom: "20px",
            fontFamily: "Sofia Pro",
            color: "#6E6E6E",
          }}
        >
          Please add the email address of the athlete you'd like to invite.
        </Typography>
        <TextField
          size="small"
          variant="filled"
          fullWidth
          type="text"
          value={email}
          style={{ marginTop: "20px" }}
          onChange={(e: any) => setEmail(e.target.value)}
          placeholder="Email"
        />

        {error.error && error.section === "invite" && (
          <>
            <Typography variant="caption" sx={{ color: "red", width: "100%" }}>
              {error.message}
            </Typography>
            <br />
          </>
        )}
        {success === "invite" && (
          <>
            <Typography
              variant="caption"
              sx={{ color: "green", width: "100%" }}
            >
              Invite sent successfully
            </Typography>
            <br />
          </>
        )}
        <SportTeamSelectors
          currentClinician={currentClinician}
          selectedSports={selectedSportsSingle}
          setSelectedSports={setSelectedSportsSingle}
          selectedTeams={selectedTeamsSingle}
          teams={teams}
          setSelectedTeams={setSelectedTeamsSingle}
        />
        <Button
          style={{ maxHeight: "40px", marginTop: "20px" }}
          title={loading === "invite" ? "Loading..." : "Send  invite"}
          onClick={handleSendInvite}
          disabled={loading === "invite" || emailIsValid !== ""}
          type={ButtonType.normal}
          colorScheme={
            loading === "invite" || emailIsValid !== ""
              ? ButtonColorScheme.disabled
              : ButtonColorScheme.gradient
          }
        />
      </Grid>
      <Grid item xs={5} sx={{ paddingLeft: "20px" }}>
        <h2
          style={{
            marginBottom: "20px",
            fontFamily: "Sofia Pro",
            fontSize: "24px",
          }}
        >
          Batch Email list invite
        </h2>
        <Typography
          style={{
            marginBottom: "20px",
            fontFamily: "Sofia Pro",
            color: "#6E6E6E",
          }}
        >
          Paste the emails separated by a comma.
        </Typography>

        <TextField
          fullWidth
          sx={{ mt: 4 }}
          error={Boolean(errorMessage)}
          label="Paste email list separated by comma"
          placeholder="Paste email list separated by comma, For example, john@gmail.com, sally@gmail.com"
          multiline
          value={emailList}
          onChange={(event) => {
            const raw = event?.target.value ?? "";

            // Convert breaklines, spaces and semicolons to commas, then normalize commas
            let normalized = raw.replace(/[\n\r;]+/g, ",");
            normalized = normalized.replace(/\s+/g, ",");
            normalized = normalized.replace(/\s*,\s*/g, ","); // remove spaces around commas
            normalized = normalized.replace(/,+/g, ","); // collapse multiple commas
            // Do not strip trailing/leading commas so users can type a comma

            const emails = normalized
              ? normalized
                  .split(",")
                  .map((e) => e.trim())
                  .filter(Boolean)
              : [];

            if (emails.length > 50) {
              return setErrorMessage(
                `50 emails allowed per batch, current email count: ${emails.length}`
              );
            }
            setErrorMessage("");
            setEmailList(normalized);
          }}
          rows={5}
        />

        <Typography
          variant="caption"
          sx={errorMessage ? { color: "red", width: "100%" } : {}}
        >
          {Boolean(errorMessage)
            ? errorMessage
            : " For example, john@gmail.com,sally@gmail.com,charles@gmail.com"}
        </Typography>
        <br />

        {error.error && error.section === "batch" && (
          <>
            <Typography variant="caption" sx={{ color: "red", width: "100%" }}>
              {error.message}
            </Typography>
            <br />
          </>
        )}

        {success === "batch" && (
          <>
            <Typography
              variant="caption"
              sx={{ color: "green", width: "100%" }}
            >
              Batch invite sent successfully
            </Typography>
            <br />
          </>
        )}

        <SportTeamSelectors
          teams={teams}
          currentClinician={currentClinician}
          selectedSports={selectedSportsBatch}
          setSelectedSports={setSelectedSportsBatch}
          selectedTeams={selectedTeamsBatch}
          setSelectedTeams={setSelectedTeamsBatch}
        />
        <Button
          style={{ maxHeight: "40px", marginTop: "20px" }}
          title={loading === "batch" ? "Loading..." : "Send batch invite"}
          onClick={handleSendBatchInvite}
          type={ButtonType.normal}
          colorScheme={
            loading === "batch"
              ? ButtonColorScheme.disabled
              : ButtonColorScheme.gradient
          }
        />
      </Grid>
    </Grid>
  );
};

export default InviteAthletePage;
